import React, { useEffect, useRef } from 'react';

export interface TripPoint {
  lat: number;
  lon: number;
  timestamp: string;
  speed: number;
}

interface LatLng {
  lat: number;
  lng: number;
}

interface TripRouteMapProps {
  points: TripPoint[];
  /** Unique map container id – must be unique per page if multiple maps are rendered */
  mapId?: string;
  /**
   * When provided, the map renders a Smartcar estimated route instead of a GPS polyline.
   * Both start and end must be supplied together.
   */
  smartcarStart?: LatLng;
  smartcarEnd?: LatLng;
}

/**
 * Renders a Leaflet OpenStreetMap with:
 *  - Blue polyline for the full route (OBD / GPS trips)
 *  - Green marker at start, red marker at end
 *
 * Smartcar trips (no waypoints): renders a dashed indigo line between
 * start and end coordinates with an informational banner.
 *
 * Leaflet is imported dynamically to avoid SSR issues and keep the bundle lean.
 * The map element must have an explicit height set via className / style.
 */
export const TripRouteMap: React.FC<TripRouteMapProps> = ({
  points,
  mapId = 'trip-route-map',
  smartcarStart,
  smartcarEnd,
}) => {
  const mapRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const isSmartcar = !!(smartcarStart && smartcarEnd);

  useEffect(() => {
    const hasObdPoints = points && points.length > 0;
    if (!hasObdPoints && !isSmartcar) return;

    import('leaflet').then((L) => {
      // Fix default icon paths broken by bundlers
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      delete (L.Icon.Default.prototype as any)._getIconUrl;
      L.Icon.Default.mergeOptions({
        iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
        iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
        shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
      });

      // Destroy previous map instance if the container is reused
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }

      const container = document.getElementById(mapId);
      if (!container) return;

      // --- Start marker icon (green dot) ---
      const startIcon = L.divIcon({
        html: `<div style="width:14px;height:14px;border-radius:50%;background:#16a34a;border:3px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.5)"></div>`,
        className: '',
        iconSize: [14, 14],
        iconAnchor: [7, 7],
      });

      // --- End marker icon (red dot) ---
      const endIcon = L.divIcon({
        html: `<div style="width:14px;height:14px;border-radius:50%;background:#dc2626;border:3px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.5)"></div>`,
        className: '',
        iconSize: [14, 14],
        iconAnchor: [7, 7],
      });

      if (isSmartcar && smartcarStart && smartcarEnd) {
        // ---- Smartcar branch: dashed straight line between start and end ----
        const startLatLng: [number, number] = [smartcarStart.lat, smartcarStart.lng];
        const endLatLng: [number, number] = [smartcarEnd.lat, smartcarEnd.lng];

        const midLat = (smartcarStart.lat + smartcarEnd.lat) / 2;
        const midLng = (smartcarStart.lng + smartcarEnd.lng) / 2;

        const map = L.map(mapId).setView([midLat, midLng], 12);
        mapRef.current = map;

        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
          maxZoom: 19,
        }).addTo(map);

        const dashedLine = L.polyline([startLatLng, endLatLng], {
          color: '#6366f1',
          weight: 2,
          dashArray: '8 6',
          opacity: 0.8,
        }).addTo(map);

        map.fitBounds(dashedLine.getBounds(), { padding: [32, 32] });

        L.marker(startLatLng, { icon: startIcon })
          .addTo(map)
          .bindPopup('<strong>Départ</strong>');

        L.marker(endLatLng, { icon: endIcon })
          .addTo(map)
          .bindPopup('<strong>Arrivée</strong>');
      } else {
        // ---- OBD / GPS / Smartcar-with-waypoints branch: solid polyline ----
        const avgLat = points.reduce((s, p) => s + p.lat, 0) / points.length;
        const avgLon = points.reduce((s, p) => s + p.lon, 0) / points.length;

        const map = L.map(mapId).setView([avgLat, avgLon], 13);
        mapRef.current = map;

        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
          maxZoom: 19,
        }).addTo(map);

        const latlngs: [number, number][] = points.map((p) => [p.lat, p.lon]);

        const polyline = L.polyline(latlngs, {
          color: '#2563EB',
          weight: 4,
          opacity: 0.85,
        }).addTo(map);

        map.fitBounds(polyline.getBounds(), { padding: [24, 24] });

        const first = points[0];
        const last = points[points.length - 1];

        // Small dot icon for intermediate waypoints
        const waypointIcon = L.divIcon({
          html: `<div style="width:8px;height:8px;border-radius:50%;background:#2563EB;border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,.4)"></div>`,
          className: '',
          iconSize: [8, 8],
          iconAnchor: [4, 4],
        });

        // Add intermediate waypoint markers (skip first and last)
        points.slice(1, -1).forEach((p) => {
          L.marker([p.lat, p.lon], { icon: waypointIcon })
            .addTo(map)
            .bindPopup(`${new Date(p.timestamp).toLocaleTimeString()}`);
        });

        L.marker([first.lat, first.lon], { icon: startIcon })
          .addTo(map)
          .bindPopup(
            `<strong>Départ</strong><br/>${new Date(first.timestamp).toLocaleTimeString()}`
          );

        L.marker([last.lat, last.lon], { icon: endIcon })
          .addTo(map)
          .bindPopup(
            `<strong>Arrivée</strong><br/>${new Date(last.timestamp).toLocaleTimeString()}`
          );
      }
    });

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
    // Re-run whenever the relevant inputs change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [points, mapId, isSmartcar, smartcarStart?.lat, smartcarStart?.lng, smartcarEnd?.lat, smartcarEnd?.lng]);

  return (
    <div ref={containerRef} className="relative w-full rounded-lg overflow-hidden border border-gray-200">
      {/* Leaflet CSS – injected once globally via index.html or here via a link tag */}
      <link
        rel="stylesheet"
        href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css"
        // @ts-expect-error integrity is a valid HTML attribute
        integrity="sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY="
        crossOrigin=""
      />

      {/* Smartcar estimated-route banner */}
      {isSmartcar && (
        <div className="absolute top-2 right-2 z-[1000] flex items-center gap-1.5 bg-indigo-50 border border-indigo-200 text-indigo-700 text-xs font-medium px-3 py-1.5 rounded-md shadow-sm pointer-events-none max-w-[calc(100%-6rem)]">
          <svg className="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M13 16h-1v-4h-1m1-4h.01M12 3a9 9 0 110 18A9 9 0 0112 3z" />
          </svg>
          Trajet Smartcar — itinéraire estimé (pas de GPS en temps réel)
        </div>
      )}

      {/* Legend */}
      <div className="absolute top-2 left-2 z-[1000] flex flex-col gap-1 bg-white bg-opacity-90 rounded-md px-3 py-2 shadow text-xs text-gray-700 pointer-events-none">
        <div className="flex items-center gap-2">
          <span className="inline-block w-3 h-3 rounded-full bg-green-600 border-2 border-white shadow" />
          Start
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-block w-3 h-3 rounded-full bg-red-600 border-2 border-white shadow" />
          End
        </div>
        {isSmartcar && (
          <div className="flex items-center gap-2 mt-0.5">
            <span
              className="inline-block w-6 shrink-0"
              style={{ borderTop: '2px dashed #6366f1' }}
            />
            <span className="text-indigo-600">Estimé</span>
          </div>
        )}
      </div>

      <div id={mapId} style={{ height: '320px', width: '100%' }} />
    </div>
  );
};
